import type { ReactNode } from 'react';
import { Check, Info } from 'lucide-react';
import { Box } from './box';
import { Button, type CustomButtonProps } from './button';
import { CloseIcon } from './closeIcon';
import { pressWash } from './press';

// Emerald/Toast (1666:20631): the AppModal surface lifted onto the screen.
// Shared by the Chakra toaster (toaster.tsx) and the update / offline notice
// (UpdateNotification.tsx), which has its own two-button layout.
export const TOAST_SURFACE = {
  w: 'min(358px, calc(100vw - 2rem))',
  bg: 'spades.surface',
  backgroundImage: 'var(--spades-panel-gradient)',
  border: '1.5px solid',
  borderColor: 'var(--spades-border-strong)',
  borderRadius: 'var(--spades-radius-lg)',
  boxShadow:
    '0 16px 20px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
  color: 'var(--spades-ivory)',
} as const;

// Emerald/Toast insets that have no spacing token (14 around the edge, 2 between
// title and description).
export const TOAST_INSET = '14px';
export const TOAST_TEXT_GAP = '2px';

// AppModal's X: the same slot in the same corner.
const CLOSE_TOP = '12px';
const CLOSE_RIGHT = '14px';
const CLOSE_SIZE = '44px';
const CLOSE_ICON = '32px';
/** Padding that keeps text beside the X from running under it. */
export const TOAST_CLOSE_CLEARANCE = CLOSE_SIZE;

/** For a notice whose surface is position: relative. */
export const ToastCloseButton = (props: CustomButtonProps) => (
  <Button
    variant="ghost"
    aria-label="Dismiss"
    position="absolute"
    top={CLOSE_TOP}
    right={CLOSE_RIGHT}
    w={CLOSE_SIZE}
    h={CLOSE_SIZE}
    minW={CLOSE_SIZE}
    minH={CLOSE_SIZE}
    p={0}
    color="spades.ivory"
    css={{
      ...pressWash,
      // Chakra's button recipe sizes icons; keep AppModal's.
      '& svg': { width: CLOSE_ICON, height: CLOSE_ICON },
    }}
    {...props}
  >
    <CloseIcon />
  </Button>
);

/** Success wears the felt badge with a mint ring, everything else the dark
 *  badge with a maple ring, around `icon` when it has its own. */
export const ToastIconBadge = ({
  success = false,
  icon,
}: {
  success?: boolean;
  icon?: ReactNode;
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    w="36px"
    h="36px"
    borderRadius="var(--spades-radius-pill)"
    border="1.5px solid"
    bg={success ? 'var(--spades-felt-dark)' : 'var(--spades-background)'}
    borderColor={success ? 'var(--spades-mint)' : 'var(--spades-accent-base)'}
    color="var(--spades-ivory)"
  >
    {icon ?? (success ? <Check size={18} /> : <Info size={18} />)}
  </Box>
);
